import React, { useState, useEffect } from "react";
import ComplaintService, {
  CreateComplaintData,
  PatientComplaint,
  UpdateComplaintData,
} from "../../../../services/Complaintservice";

interface ComplaintsModalProps {
  show: boolean;
  onHide: () => void;
  patientId: number;
  patientName?: string;
  complaint?: PatientComplaint | null;
  onSuccess: () => void;
}

const ComplaintsModal: React.FC<ComplaintsModalProps> = ({
  show,
  onHide,
  patientId,
  patientName = "Patient",
  complaint,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    complaint: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const isEditMode = !!complaint;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (complaint) {
      setFormData({
        complaint: complaint.complaint,
      });
    } else {
      setFormData({
        complaint: "",
      });
    }
    setError(null);
  }, [complaint, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditMode && complaint?.id) {
        const updateData: UpdateComplaintData = {
          complaint: formData.complaint,
        };
        await ComplaintService.updateComplaint(complaint.id, updateData);
      } else {
        const createData: CreateComplaintData = {
          patient_id: patientId,
          complaint: formData.complaint,
        };
        await ComplaintService.createComplaint(createData);
      }

      onSuccess();
      onHide();
    } catch (err: any) {
      setError(err.message || "Failed to save complaint");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onHide}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="header-info">
              <h2>{isEditMode ? "Edit Complaint" : "Record New Complaint"}</h2>
              <p>Patient: {patientName}</p>
            </div>
            <button
              type="button"
              className="close-button"
              onClick={onHide}
              disabled={loading}
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-content">
              {error && (
                <div className="error-alert">
                  <span>{error}</span>
                  <button
                    type="button"
                    onClick={() => setError(null)}
                    className="alert-close"
                  >
                    ×
                  </button>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Complaint *</label>
                <textarea
                  className="form-input textarea"
                  rows={6}
                  value={formData.complaint}
                  onChange={(e) =>
                    setFormData({ ...formData, complaint: e.target.value })
                  }
                  placeholder="Enter patient's complaint..."
                  required
                  disabled={loading}
                />
                <small className="form-text">
                  This complaint will be recorded with the current date and time
                </small>
              </div>
            </div>

            <div className="modal-footer">
              <div className="footer-buttons">
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={onHide}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="button button-primary"
                  disabled={loading}
                >
                  {loading
                    ? "Saving..."
                    : isEditMode
                    ? "Update Complaint"
                    : "Record Complaint"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <style>
        {`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          z-index: 1100;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: ${isMobile ? "0" : "20px"};
          overflow-y: auto;
        }

        .modal-container {
          background: white;
          border-radius: ${isMobile ? "0" : "12px"};
          width: ${isMobile ? "100%" : isTablet ? "90%" : "600px"};
          max-width: ${isMobile ? "100%" : "90vw"};
          max-height: ${isMobile ? "100%" : "90vh"};
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          margin: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: ${isMobile ? "16px" : "20px 24px"};
          border-bottom: 1px solid #e5e7eb;
          background: white;
          border-top-left-radius: ${isMobile ? "0" : "12px"};
          border-top-right-radius: ${isMobile ? "0" : "12px"};
        }

        .header-info {
          flex: 1;
        }

        .header-info h2 {
          font-size: ${isMobile ? "18px" : "20px"};
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 4px 0;
        }

        .header-info p {
          font-size: ${isMobile ? "13px" : "14px"};
          color: #6b7280;
          margin: 0;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 24px;
          color: #6b7280;
          cursor: pointer;
          padding: 4px 8px;
          line-height: 1;
          transition: color 0.2s ease;
          margin-left: 16px;
        }

        .close-button:hover:not(:disabled) {
          color: #1f2937;
        }

        .close-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .modal-content {
          padding: ${isMobile ? "16px" : "24px"};
          overflow-y: auto;
          flex: 1;
        }

        .error-alert {
          background: #fee2e2;
          border: 1px solid #ef4444;
          border-radius: 8px;
          padding: 12px 16px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #991b1b;
          font-size: 14px;
        }

        .alert-close {
          background: none;
          border: none;
          font-size: 20px;
          color: #991b1b;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 8px;
        }

        .form-input {
          width: 100%;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 14px;
          transition: border-color 0.2s ease;
          font-family: inherit;
        }

        .form-input:focus {
          border-color: #3b82f6;
          outline: none;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-input:disabled {
          background-color: #f3f4f6;
          cursor: not-allowed;
        }

        .form-input::placeholder {
          color: #9ca3af;
        }

        .textarea {
          resize: vertical;
          min-height: 120px;
        }

        .form-text {
          display: block;
          font-size: 12px;
          color: #6b7280;
          margin-top: 4px;
        }

        .modal-footer {
          padding: ${isMobile ? "16px" : "16px 24px"};
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
          border-bottom-left-radius: ${isMobile ? "0" : "12px"};
          border-bottom-right-radius: ${isMobile ? "0" : "12px"};
        }

        .footer-buttons {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .button {
          padding: ${isMobile ? "12px 20px" : "10px 20px"};
          border-radius: 8px;
          border: none;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: ${isMobile ? "auto" : "120px"};
        }

        .button:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .button-primary {
          background: #3b82f6;
          color: white;
        }

        .button-primary:hover:not(:disabled) {
          background: #2563eb;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .button-secondary {
          background: #e5e7eb;
          color: #374151;
        }

        .button-secondary:hover:not(:disabled) {
          background: #d1d5db;
        }

        /* Mobile adjustments */
        ${
          isMobile
            ? `
          .footer-buttons {
            flex-direction: column-reverse;
          }

          .button {
            width: 100%;
          }
        `
            : ""
        }

        /* Scrollbar styles */
        .modal-content::-webkit-scrollbar {
          width: 8px;
        }

        .modal-content::-webkit-scrollbar-track {
          background: #f1f5f9;
        }

        .modal-content::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }

        .modal-content::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}
      </style>
    </>
  );
};

export default ComplaintsModal;
